import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DraftEditor } from "@/components/DraftEditor";
import { SavedDraftsTab } from "@/components/tabs/SavedDraftsTab";
import { ScheduledPostsTab } from "@/components/tabs/ScheduledPostsTab";

const Index = () => {
  const [activeTab, setActiveTab] = useState("drafts");
  const [savedDrafts, setSavedDrafts] = useState<Array<{ id: string, content: string, created_at: string }>>([]);
  const [scheduledPosts, setScheduledPosts] = useState<Array<{
    id: string;
    content: string;
    scheduled_for: string;
    timezone: string;
    created_at: string;
  }>>([]);

  useEffect(() => {
    fetchSavedDrafts();
    fetchScheduledPosts();
  }, []);

  const fetchSavedDrafts = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error("Please sign in to view drafts");
      return;
    }

    const { data: drafts, error } = await supabase
      .from('drafts')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error("Failed to fetch drafts");
      return;
    }

    setSavedDrafts(drafts || []);
  };

  const fetchScheduledPosts = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return;
    }

    const { data: posts, error } = await supabase
      .from('scheduled_posts')
      .select('*')
      .order('scheduled_for', { ascending: true });

    if (error) {
      toast.error("Failed to fetch scheduled posts");
      return;
    }

    setScheduledPosts(posts || []);
  };

  const deleteDraft = async (draftId: string) => {
    const { error } = await supabase
      .from('drafts')
      .delete()
      .eq('id', draftId);

    if (error) {
      toast.error("Failed to delete draft");
      return;
    }

    toast.success("Draft deleted successfully");
    fetchSavedDrafts();
  };

  const deleteScheduledPost = async (postId: string) => {
    const { error } = await supabase
      .from('scheduled_posts')
      .delete()
      .eq('id', postId);

    if (error) {
      toast.error("Failed to delete scheduled post");
      return;
    }

    toast.success("Scheduled reminder deleted successfully");
    fetchScheduledPosts();
  };

  return (
    <div className="container px-4 py-4 max-w-5xl">
      <Tabs defaultValue="drafts" className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="drafts">New Draft</TabsTrigger>
          <TabsTrigger value="saved">Saved Drafts</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
        </TabsList>

        <TabsContent value="drafts">
          <DraftEditor
            onSaveDraft={() => {
              fetchSavedDrafts();
              setActiveTab('saved');
            }}
            onSchedule={() => {
              fetchScheduledPosts();
              setActiveTab('scheduled');
            }}
          />
        </TabsContent>

        <TabsContent value="saved">
          <SavedDraftsTab
            drafts={savedDrafts}
            onDeleteDraft={deleteDraft}
            onDraftUpdated={fetchSavedDrafts}
          />
        </TabsContent>

        <TabsContent value="scheduled">
          <ScheduledPostsTab
            posts={scheduledPosts}
            onDeleteScheduled={deleteScheduledPost}
            onScheduledUpdated={fetchScheduledPosts}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Index;